import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
  Typography,
  useTheme,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  People as CustomersIcon,
  ViewList as ProductsIcon,
  Assessment as ReportsIcon,
  Group as TeamIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

const drawerWidth = 220;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Orders', icon: <OrdersIcon />, path: '/orders' },
  { text: 'Customers', icon: <CustomersIcon />, path: '/customers' },
  { text: 'Products', icon: <ProductsIcon />, path: '/products' },
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
  { text: 'Team Directory', icon: <TeamIcon />, path: '/users' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const adminMenuItems = [
  { text: 'System Admin', icon: <AdminIcon />, path: '/admin' },
];

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: any) => state.auth.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? false : true} // Always closed on mobile since we use bottom nav
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'none', sm: 'block' }, // Hide on mobile
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', mt: 9 }}>
        <List dense disablePadding sx={{ px: 1, pt: 1.5, pb: 1, flexGrow: 1, overflowY: 'auto' }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ my: 0.25 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 36,
                  px: 1.25,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                    minWidth: 28,
                  }}
                >
                  <Box sx={{ '& svg': { fontSize: '1.1rem' } }}>{item.icon}</Box>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {isAdmin && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ px: 2, pt: 0.5, pb: 0.25 }}>
              <Typography variant="overline" color="text.secondary">
                Administration
              </Typography>
            </Box>
            <List dense disablePadding sx={{ px: 1, pb: 0.5 }}>
              {adminMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ my: 0.25 }}>
                <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: 36,
                    px: 1.25,
                    '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                      '&:hover': {
                          backgroundColor: theme.palette.primary.light,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                        color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                        minWidth: 28,
              }}
            >
                      <Box sx={{ '& svg': { fontSize: '1.1rem' } }}>{item.icon}</Box>
            </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={item.text} />
          </ListItemButton>
                </ListItem>
              ))}
        </List>
          </>
        )}
        <Box sx={{ px: 1, pb: 1 }}>
          <ListItem component={Link} to="/settings/notifications" disablePadding>
            <ListItemButton sx={{ minHeight: 36, px: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Box sx={{ '& svg': { fontSize: '1.1rem' } }}><NotificationsIcon /></Box>
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary="Notification Settings" />
            </ListItemButton>
          </ListItem>
        </Box>

        {/* Footer / Logout anchored to bottom */}
        <Box sx={{ mt: 'auto', px: 1, pb: 1, display: { xs: 'none', md: 'block' } }}>
          <Tooltip title="Sign out">
            <ListItemButton
              onClick={async () => {
                try {
                  // Clear local auth and navigate
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                } finally {
                  navigate('/login');
                }
              }}
              sx={{
                minHeight: 36,
                px: 1.25,
                borderRadius: 1,
                bgcolor: (theme) => theme.palette.error.light + '20',
                color: 'error.main',
                '&:hover': { bgcolor: (theme) => theme.palette.error.light + '35' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: 'error.main' }}>
                {/* Using AdminIcon as a placeholder sign-out icon to avoid extra imports */}
                <Box sx={{ '& svg': { fontSize: '1.1rem' } }}><AdminIcon /></Box>
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} primary="Logout" />
            </ListItemButton>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;